import React, { useEffect, useMemo, useState } from 'react'
import { BrowserRouter, Routes, Route, Link, useParams, useNavigate } from 'react-router-dom'
import { Toaster, toast } from 'sonner'
import { nanoid } from 'nanoid'
import { LoomCanvas } from './components/LoomCanvas'
import { ShuttleForm } from './components/ShuttleForm'
import { InsightCharts } from './components/InsightCharts'
import type { Thread, Loom, Echo, Aggregates } from './lib/types'
import { computeAggregates, makeManifest, selectEchoes } from './lib/aggregation'
import { generateDemoThreads, DEMO_LOOM_TITLE } from './lib/demoData'
import { ArrowRight, Users, Share2, Download, Eye, Lock, Home, Plus, Clock } from 'lucide-react'

// Local storage keys
const MY_LOOMS_KEY = 'loom_my_looms_v1'
const OWNER_SECRETS_KEY = 'loom_owner_secrets_v1'

interface MyLoom {
  joinCode: string
  title: string
  lastVisited: string
}

function useMyLooms() {
  const [myLooms, setMyLooms] = useState<MyLoom[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(MY_LOOMS_KEY) || '[]')
    } catch { return [] }
  })

  const add = (loom: MyLoom) => {
    setMyLooms(prev => {
      const filtered = prev.filter(l => l.joinCode !== loom.joinCode)
      const next = [{ ...loom, lastVisited: new Date().toISOString() }, ...filtered].slice(0, 6)
      localStorage.setItem(MY_LOOMS_KEY, JSON.stringify(next))
      return next
    })
  }

  return { myLooms, add }
}

function useOwnerSecret(loomId: string) {
  const [secret, setSecret] = useState<string | null>(() => {
    try {
      const map = JSON.parse(localStorage.getItem(OWNER_SECRETS_KEY) || '{}')
      return map[loomId] || null
    } catch { return null }
  })

  const save = (newSecret: string) => {
    const map = JSON.parse(localStorage.getItem(OWNER_SECRETS_KEY) || '{}')
    map[loomId] = newSecret
    localStorage.setItem(OWNER_SECRETS_KEY, JSON.stringify(map))
    setSecret(newSecret)
  }

  return { secret, save }
}

// ---------- LANDING ----------
function Landing() {
  const { myLooms } = useMyLooms()
  const [showDemo, setShowDemo] = useState(false)
  const demoThreads = useMemo(() => generateDemoThreads(9), [])

  return (
    <div className="min-h-screen bg-[#f8f4ed] text-[#3f2a1d]">
      <header className="border-b border-[#d4c8b5] bg-white/70 backdrop-blur">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded bg-[#2f3459] flex items-center justify-center text-white text-xl leading-none pt-0.5">L</div>
            <div>
              <div className="font-semibold tracking-tight text-xl">Loom</div>
              <div className="text-[10px] text-[#8a7560] -mt-1">EMOTION NAMING LOOM</div>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <a href="https://www.linkedin.com/in/mindful-abhay/" target="_blank" className="hover:underline">LinkedIn</a>
            <a href="https://buymeacoffee.com/abhaypratapsingh" target="_blank" className="hover:underline">Buy Me a Coffee</a>
            <Link to="/loom/demo" className="px-4 py-1.5 rounded bg-[#2f3459] text-white text-sm flex items-center gap-1.5">
              Try the loom <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 pt-16 pb-12">
        <div className="max-w-3xl">
          <div className="uppercase tracking-[3px] text-xs text-[#9c6644] mb-3">FOR FIRST-GENERATION ROOMMATES</div>
          <h1 className="text-6xl leading-[0.95] tracking-[-2.2px] font-medium mb-4">
            Name what the system<br />makes invisible.<br />Weave it into cloth.
          </h1>
          <p className="text-xl text-[#5c4636] max-w-[38ch]">
            A public artifact generator for the cognitive load of being first-generation.
            Spaced repetition. No journals. No names. Just the loom.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/create" className="shuttle-btn inline-flex items-center gap-2 px-8 py-3.5 rounded bg-[#2f3459] text-white text-base font-medium">
            Create a new loom <Plus size={18} />
          </Link>
          <Link to="/join" className="inline-flex items-center gap-2 px-8 py-3.5 rounded border border-[#d4c8b5] hover:bg-white text-base">
            Join with a code
          </Link>
          <button onClick={() => setShowDemo(!showDemo)} className="inline-flex items-center gap-2 px-5 py-3.5 rounded border border-[#d4c8b5] hover:bg-white text-base">
            <Eye size={17} /> See a live weave
          </button>
        </div>

        {myLooms.length > 0 && (
          <div className="mt-14">
            <div className="uppercase text-xs tracking-widest text-[#8a7560] mb-3">YOUR LOOMS</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {myLooms.map(l => (
                <Link key={l.joinCode} to={`/loom/${l.joinCode}`} className="card p-4 hover:border-[#9c6644] flex justify-between items-start group">
                  <div>
                    <div className="font-medium text-lg leading-tight">{l.title}</div>
                    <div className="text-xs text-[#8a7560] mt-px">code: {l.joinCode}</div>
                  </div>
                  <ArrowRight className="mt-1 opacity-40 group-hover:opacity-100 transition" size={18} />
                </Link>
              ))}
            </div>
          </div>
        )}

        {(showDemo || window.location.hash.includes('demo')) && (
          <div className="mt-16">
            <div className="flex items-center gap-3 mb-3 text-sm text-[#8a7560]">
              <div className="h-px flex-1 bg-[#d4c8b5]" /> LIVE DEMO WEAVE — NO DATA LEAVES YOUR BROWSER <div className="h-px flex-1 bg-[#d4c8b5]" />
            </div>
            <LoomCanvas threads={demoThreads} title={DEMO_LOOM_TITLE} onExport={(url) => {
              const a = document.createElement('a')
              a.href = url; a.download = 'loom-demo.png'; a.click()
            }} />
            <div className="text-center mt-4 text-sm text-[#6b5c4a]">
              This is what a real loom looks like after 9 threads. Try throwing your own in the demo room.
            </div>
          </div>
        )}

        <div className="mt-20 grid md:grid-cols-3 gap-x-8 gap-y-10 text-sm border-t border-[#d4c8b5] pt-10">
          <div>
            <div className="font-medium mb-1.5">The four axes</div>
            <div className="text-[#6b5c4a]">Attention fragmentation. Recovery need. Readiness state. Post-reset clarity. Every thread carries all four.</div>
          </div>
          <div>
            <div className="font-medium mb-1.5">Spaced repetition, collective</div>
            <div className="text-[#6b5c4a]">Cycles surface old emotion names as “echoes”. The group re-names how the same load feels days or weeks later. The cloth tightens.</div>
          </div>
          <div>
            <div className="font-medium mb-1.5">Public artifact, private threads</div>
            <div className="text-[#6b5c4a]">Anyone can see the woven image + the manifest of named emotions. Only people with the join code ever see the actual notes.</div>
          </div>
        </div>
      </div>

      <footer className="border-t border-[#d4c8b5] mt-12 py-8 text-xs text-[#8a7560]">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row gap-y-1 items-center justify-between">
          <div>Built with care by Abhay • <a href="https://www.linkedin.com/in/mindful-abhay/" className="underline">LinkedIn</a> • <a href="https://buymeacoffee.com/abhaypratapsingh" className="underline">Buy Me a Coffee</a></div>
          <div>No accounts. No tracking. The loom belongs to the roommates who weave it.</div>
        </div>
      </footer>
      <Toaster position="top-center" richColors closeButton />
    </div>
  )
}

// ---------- CREATE ----------
function CreateLoom() {
  const navigate = useNavigate()
  const { add } = useMyLooms()
  const [title, setTitle] = useState('Our Apartment Loom')
  const [isPublic, setIsPublic] = useState(true)
  const [creating, setCreating] = useState(false)

  async function create() {
    setCreating(true)
    try {
      // In a real deploy this calls the Worker. For now we simulate a beautiful local loom.
      const joinCode = nanoid(6).toUpperCase().slice(0, 6)
      const loomId = 'loom_' + nanoid(10)
      const ownerSecret = nanoid(18)

      const newLoom: MyLoom = { joinCode, title, lastVisited: new Date().toISOString() }

      // Store owner secret locally
      const map = JSON.parse(localStorage.getItem(OWNER_SECRETS_KEY) || '{}')
      map[loomId] = ownerSecret
      localStorage.setItem(OWNER_SECRETS_KEY, JSON.stringify(map))

      add(newLoom)

      // Navigate into the room — the room will run in demo+local mode until backend is connected
      navigate(`/loom/${joinCode}?owner=1&title=${encodeURIComponent(title)}`)
      toast.success('Loom created. Share the code with your roommates.')
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="max-w-md mx-auto px-5 pt-12">
      <Link to="/" className="text-sm flex items-center gap-1 text-[#8a7560] hover:text-[#3f2a1d]"><Home size={15}/> back</Link>
      <h1 className="loom-title text-4xl mt-6 tracking-tight">Create a loom</h1>
      <p className="text-[#6b5c4a] mt-1">Your roommates will join with a short code. Everything stays anonymous inside the room.</p>

      <div className="card mt-8 p-6 space-y-5">
        <div>
          <label className="text-sm block mb-1.5">Loom title (visible on the artifact)</label>
          <input value={title} onChange={e => setTitle(e.target.value)} className="w-full border border-[#d4c8b5] px-3 py-2 rounded bg-white" maxLength={60} />
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={isPublic} onChange={e => setIsPublic(e.target.checked)} />
          Allow a public artifact page to be generated and shared
        </label>

        <button onClick={create} disabled={creating || !title.trim()} className="shuttle-btn w-full py-3 bg-[#2f3459] text-white rounded font-medium disabled:bg-[#5c4636]">
          {creating ? 'Creating…' : 'Create loom + get join code'}
        </button>
      </div>

      <p className="text-xs text-[#8a7560] mt-4 text-center">You will receive an owner secret stored only in this browser. Use it to advance cycles and view deeper insights.</p>
    </div>
  )
}

// ---------- JOIN ----------
function JoinLoom() {
  const navigate = useNavigate()
  const [code, setCode] = useState('')
  const [joining, setJoining] = useState(false)

  async function join() {
    if (!code.trim()) return
    setJoining(true)
    const clean = code.trim().toUpperCase()
    // For now any 4+ char code works in demo/local mode. Real version validates against Worker.
    setTimeout(() => {
      navigate(`/loom/${clean}`)
    }, 120)
  }

  return (
    <div className="max-w-sm mx-auto px-5 pt-16">
      <Link to="/" className="text-sm text-[#8a7560]">← home</Link>
      <h1 className="loom-title text-4xl mt-5 tracking-tight">Join a loom</h1>
      <p className="mt-1 text-[#6b5c4a]">Enter the code your roommate gave you.</p>

      <div className="mt-7 card p-5">
        <input
          value={code}
          onChange={e => setCode(e.target.value.toUpperCase())}
          onKeyDown={e => e.key === 'Enter' && join()}
          placeholder="E7K9P2"
          className="w-full text-center tracking-[4px] text-2xl font-mono border border-[#d4c8b5] py-3 rounded"
          maxLength={8}
        />
        <button onClick={join} disabled={joining || code.length < 4} className="mt-3 w-full py-3 rounded bg-[#2f3459] text-white font-medium disabled:bg-[#5c4636]">
          Enter the room
        </button>
      </div>
      <div className="text-center text-xs mt-6 text-[#8a7560]">No account needed. The code is the only key.</div>
    </div>
  )
}

// ---------- THE ACTUAL LOOM ROOM (the product) ----------
function LoomRoom() {
  const { code } = useParams<{ code: string }>()
  const navigate = useNavigate()
  const isDemo = code === 'demo'
  const { add } = useMyLooms()
  const { secret: ownerSecret, save: saveOwnerSecret } = useOwnerSecret(code || 'demo')

  const [threads, setThreads] = useState<Thread[]>(() => isDemo ? generateDemoThreads(7) : [])
  const [title, setTitle] = useState(isDemo ? DEMO_LOOM_TITLE : 'Shared Loom')
  const [echoes, setEchoes] = useState<Echo[]>([])
  const [cycle, setCycle] = useState(1)
  const [presence, setPresence] = useState(2)
  const [resonanceWith, setResonanceWith] = useState<Echo | null>(null)
  const [showRaw, setShowRaw] = useState(false) // only inside room — roommates only
  const [isOwnerView, setIsOwnerView] = useState(!!new URLSearchParams(location.search).get('owner'))

  // Persist this loom in "my looms"
  useEffect(() => {
    if (code && code !== 'demo') {
      add({ joinCode: code, title, lastVisited: new Date().toISOString() })
    }
  }, [code, title])

  // Demo: simulate other roommates adding threads occasionally
  useEffect(() => {
    if (!isDemo) return
    const id = setInterval(() => {
      if (Math.random() > 0.7 && threads.length < 19) {
        const demo = generateDemoThreads(1)[0]
        demo.id = 'demo_live_' + nanoid(6)
        demo.createdAt = new Date().toISOString()
        setThreads(prev => [...prev, demo])
        setPresence(p => Math.min(4, Math.max(1, p + (Math.random() > 0.5 ? 1 : -1))))
      }
    }, 14500)
    return () => clearInterval(id)
  }, [isDemo, threads.length])

  const aggregates: Aggregates = useMemo(() => computeAggregates(threads), [threads])
  const manifest = useMemo(() => makeManifest(threads), [threads])

  // Current echoes (in real version these come from the last Cycle record)
  useEffect(() => {
    const newEchoes = selectEchoes(threads, echoes.map(e => e.threadId), 3)
    if (newEchoes.length) setEchoes(newEchoes)
  }, [threads.length]) // only recompute when cloth grows

  async function throwShuttle(partial: Omit<Thread, 'id' | 'loomId' | 'createdAt' | 'cycle'>) {
    const newThread: Thread = {
      ...partial,
      id: (isDemo ? 'demo_' : 't_') + nanoid(10),
      loomId: code || 'local',
      createdAt: new Date().toISOString(),
      cycle,
    }
    setThreads(prev => [...prev, newThread])
    setResonanceWith(null)

    toast.success('Thread thrown. The cloth moved.', { description: newThread.emotionName })

    // In a full deploy: POST to Worker, which writes to D1 + broadcasts via DO
    // Here we just stay local + beautiful.
  }

  function advanceCycle() {
    const next = cycle + 1
    setCycle(next)

    // Recompute echoes with the new cycle boundary
    const freshEchoes = selectEchoes(threads, [], 3)
    setEchoes(freshEchoes)

    toast('Cycle advanced', {
      description: `The loom has tightened. ${freshEchoes.length} echoes surfaced for spaced reflection.`,
    })

    // In real: this would call the Workflow. The Workflow would persist the cycle + echoes.
  }

  function captureArtifact() {
    // The LoomCanvas already offers export. We also "upload" a simulated artifact here.
    const data = (document.querySelector('canvas') as HTMLCanvasElement)?.toDataURL('image/png')
    if (data && !isDemo) {
      // In production this would POST to /api/artifacts/upload and get back an R2 URL
      toast.success('Artifact captured', { description: 'In production this would be stored in R2 and linked from the public artifact page.' })
    } else {
      toast('In a real loom this would be uploaded to R2 and become the permanent public artifact.')
    }
  }

  function reweaveFromEcho(e: Echo) {
    setResonanceWith(e)
    // Scroll form into view
    window.scrollTo({ top: 520, behavior: 'smooth' })
  }

  const isOwner = isDemo || isOwnerView || !!ownerSecret

  return (
    <div className="max-w-6xl mx-auto px-4 pb-24">
      <div className="flex items-center justify-between pt-6 pb-4 border-b border-[#d4c8b5]">
        <div>
          <Link to="/" className="text-xs tracking-widest text-[#8a7560] hover:text-[#3f2a1d]">LOOM</Link>
          <div className="loom-title text-3xl tracking-[-1.2px] leading-none mt-1">{title}</div>
          <div className="text-xs text-[#8a7560] mt-0.5 flex items-center gap-2">
            CODE <span className="font-mono tracking-[2px] text-[#3f2a1d]">{code?.toUpperCase()}</span>
            <span className="inline-block w-px h-3 bg-[#d4c8b5]" />
            CYCLE {cycle} • {threads.length} THREADS
          </div>
        </div>

        <div className="flex items-center gap-2 text-sm">
          <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-[#d4c8b5] text-[#5c4636]">
            <Users size={15} /> <span className="tabular-nums">{presence}</span> <span className="text-xs">present</span>
            <span className="presence-dot inline-block w-1.5 h-1.5 rounded-full bg-emerald-600 ml-0.5" />
          </div>

          <button onClick={() => navigate(`/artifact/${code}`)} className="flex items-center gap-1.5 px-4 py-1.5 rounded border border-[#d4c8b5] hover:bg-white">
            <Share2 size={15} /> Public artifact
          </button>

          {isOwner && (
            <button onClick={advanceCycle} className="flex items-center gap-1.5 px-4 py-1.5 rounded bg-[#2f3459] text-white text-sm">
              <Clock size={15} /> Advance cycle
            </button>
          )}

          <button onClick={captureArtifact} className="flex items-center gap-1.5 px-4 py-1.5 rounded border border-[#d4c8b5]">
            <Download size={15} /> Capture
          </button>

          <Link to="/" className="px-3 py-1.5 text-[#8a7560] hover:text-[#3f2a1d]"><Home size={17} /></Link>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6 mt-6">
        {/* THE LOOM — visual center */}
        <div className="lg:col-span-3">
          <LoomCanvas
            threads={threads}
            title={title}
            interactive
            onThreadSelect={(t) => {
              // Allow pulling any thread for re-weaving (spaced repetition moment)
              const fakeEcho: Echo = { threadId: t.id, emotionName: t.emotionName, snippet: t.note.slice(0, 78), fromCycle: t.cycle }
              reweaveFromEcho(fakeEcho)
            }}
            onExport={(dataUrl, kind) => {
              const a = document.createElement('a')
              a.href = dataUrl
              a.download = `loom-${code}-${kind}.png`
              a.click()
            }}
          />

          {/* Manifest + pattern language (what becomes public) */}
          <div className="mt-6 card p-5">
            <div className="uppercase tracking-[1.5px] text-xs text-[#9c6644] mb-2">THE MANIFEST</div>
            <div className="manifest leading-relaxed">{manifest}</div>

            <div className="mt-6 pt-5 border-t border-[#e5d9c9]">
              <div className="uppercase tracking-[1.5px] text-xs text-[#9c6644] mb-2">WHAT THE NUMBERS SAY (ANONYMOUS)</div>
              <div className="text-sm text-[#5c4636] space-y-1">
                {aggregates.patternSentences.map((s, i) => <div key={i}>• {s}</div>)}
              </div>
            </div>
          </div>
        </div>

        {/* THE RITUAL: add a thread + echoes */}
        <div className="lg:col-span-2 space-y-6">
          <div>
            <div className="flex items-center justify-between mb-2 px-0.5">
              <div className="text-sm font-medium">Throw a new thread</div>
              {resonanceWith && <button onClick={() => setResonanceWith(null)} className="text-xs text-[#9c6644]">clear resonance</button>}
            </div>
            <ShuttleForm
              onThrow={throwShuttle}
              defaultResonanceWith={resonanceWith}
              disabled={threads.length > 42}
            />
          </div>

          {/* Spaced repetition echoes */}
          {echoes.length > 0 && (
            <div>
              <div className="text-sm font-medium mb-2 px-0.5 flex items-center gap-2">
                <Clock size={15} /> Spaced echoes — re-weave these
              </div>
              <div className="space-y-2">
                {echoes.map(e => (
                  <button
                    key={e.threadId}
                    onClick={() => reweaveFromEcho(e)}
                    className="echo-card w-full text-left card p-3.5 border border-[#d4c8b5] hover:border-[#9c6644] text-sm"
                  >
                    <div className="font-medium text-[#2f3459] italic">{e.emotionName}</div>
                    <div className="text-[#6b5c4a] mt-px leading-snug">“{e.snippet}” <span className="text-[10px] text-[#8a7560]">— cycle {e.fromCycle}</span></div>
                    <div className="text-[#9c6644] text-xs mt-1">Re-weave this →</div>
                  </button>
                ))}
              </div>
              <div className="text-[10px] text-[#8a7560] px-1 mt-1">These surfaced because the group named similar loads earlier. Naming again is how the repetition works.</div>
            </div>
          )}

          {/* Owner / Loomkeeper tools */}
          {isOwner && (
            <div className="card-inset p-4 text-sm">
              <div className="font-medium mb-2 flex items-center gap-2"><Lock size={15} /> Loomkeeper view (only you)</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <div>Avg fragmentation</div><div className="tabular-nums font-medium">{aggregates.avgFrag}</div>
                <div>Avg recovery need</div><div className="tabular-nums font-medium">{aggregates.avgRecovery}</div>
                <div>Avg readiness</div><div className="tabular-nums font-medium">{aggregates.avgReadiness}</div>
                <div>Avg post-reset clarity</div><div className="tabular-nums font-medium">{aggregates.avgClarity}</div>
              </div>
              <button onClick={advanceCycle} className="mt-4 w-full text-xs py-2 border border-[#b39e82] rounded hover:bg-white">Advance to next spaced cycle (Workflow in prod)</button>

              <div className="mt-4">
                <InsightCharts aggregates={aggregates} />
              </div>
            </div>
          )}

          {/* Raw threads — visible only inside the trusted room */}
          <div>
            <button onClick={() => setShowRaw(!showRaw)} className="text-xs flex items-center gap-1 text-[#8a7560] hover:text-[#3f2a1d]">
              {showRaw ? 'Hide' : 'Show'} private thread text <span className="text-[10px]">(roommates only)</span>
            </button>
            {showRaw && (
              <div className="mt-2 space-y-2 text-sm">
                {threads.slice().reverse().map(t => (
                  <div key={t.id} className="card p-3 text-xs leading-snug border-l-2 border-[#9c6644]">
                    <span className="font-medium text-[#2f3459]">{t.emotionName}</span> — {t.note}
                    <div className="text-[10px] text-[#8a7560] mt-px tabular-nums">f{t.frag} r{t.recovery} rd{t.readiness} c{t.clarity}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="text-center mt-10 text-[10px] text-[#8a7560]">
        Everything you see here stays between the people who have this code. The public artifact contains only the woven image and the manifest of emotion names.
      </div>
    </div>
  )
}

// ---------- PUBLIC ARTIFACT (what gets shared) ----------
function PublicArtifact() {
  const { id } = useParams()
  const isDemo = id === 'demo'
  const threads = useMemo(() => generateDemoThreads(11), [])
  const aggregates = computeAggregates(threads)
  const manifest = makeManifest(threads)

  return (
    <div className="max-w-3xl mx-auto px-5 py-10">
      <div className="mb-6 flex justify-between items-center text-sm">
        <Link to="/" className="text-[#8a7560]">← Loom</Link>
        <div className="text-[#8a7560]">PUBLIC ARTIFACT</div>
      </div>

      <h1 className="loom-title text-4xl tracking-tight mb-1">{isDemo ? DEMO_LOOM_TITLE : 'Shared First-Gen Loom'}</h1>
      <div className="text-sm text-[#6b5c4a] mb-6">A collective record of cognitive load • {threads.length} anonymous threads • cycle {Math.max(...threads.map(t => t.cycle))}</div>

      <LoomCanvas threads={threads} title={isDemo ? DEMO_LOOM_TITLE : 'Loom'} onExport={(url) => {
        const a = document.createElement('a'); a.href = url; a.download = `artifact-${id}.png`; a.click()
      }} />

      <div className="mt-8 card p-6">
        <div className="uppercase text-xs tracking-[2px] text-[#9c6644]">THE MANIFEST</div>
        <div className="manifest mt-3">{manifest}</div>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div className="card p-5 text-sm">
          <div className="uppercase text-xs tracking-widest text-[#9c6644] mb-3">AGGREGATE LOAD (ANONYMOUS)</div>
          <div className="space-y-1 text-[#3f2a1d]">
            <div>Attention fragmentation: <span className="font-medium tabular-nums">{aggregates.avgFrag}</span>/10</div>
            <div>Recovery need: <span className="font-medium tabular-nums">{aggregates.avgRecovery}</span>/10</div>
            <div>Readiness: <span className="font-medium tabular-nums">{aggregates.avgReadiness}</span>/10</div>
            <div>Post-reset clarity: <span className="font-medium tabular-nums">{aggregates.avgClarity}</span>/10</div>
          </div>
          <div className="text-[11px] text-[#8a7560] mt-4">N = {aggregates.n}. These numbers describe the cloth, never any single person.</div>
        </div>
        <div className="card p-5 text-sm">
          <div className="uppercase text-xs tracking-widest text-[#9c6644] mb-3">PATTERN LANGUAGE</div>
          {aggregates.patternSentences.map((s, i) => <p key={i} className="mb-2 leading-snug">{s}</p>)}
        </div>
      </div>

      <div className="text-center text-xs mt-8 text-[#8a7560]">
        This page can be shared. The image can be downloaded, printed, or posted. No raw notes ever leave the room.
      </div>

      <div className="text-center mt-4">
        <Link to={`/loom/${id}`} className="text-sm underline">Return to the room (if you have the code)</Link>
      </div>
    </div>
  )
}

// ---------- ROOT APP ----------
export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/create" element={<CreateLoom />} />
        <Route path="/join" element={<JoinLoom />} />
        <Route path="/loom/:code" element={<LoomRoom />} />
        <Route path="/artifact/:id" element={<PublicArtifact />} />
        <Route path="*" element={<div className="p-12 text-center"><Link to="/">Go home</Link></div>} />
      </Routes>
      <Toaster position="top-center" />

      {/* Global creator footer — prominent as requested */}
      <footer className="no-print mt-16 py-6 border-t border-[#d4c8b5] text-xs text-[#6b5c4a]">
        <div className="max-w-5xl mx-auto px-5 flex flex-col sm:flex-row gap-y-1 items-center justify-between">
          <div>
            Built with care by <span className="font-medium text-[#3f2a1d]">Abhay</span>
          </div>
          <div className="flex gap-4">
            <a href="https://www.linkedin.com/in/mindful-abhay/" target="_blank" rel="noreferrer" className="hover:text-[#3f2a1d] underline underline-offset-2">linkedin.com/in/mindful-abhay</a>
            <a href="https://buymeacoffee.com/abhaypratapsingh" target="_blank" rel="noreferrer" className="hover:text-[#3f2a1d] underline underline-offset-2">buymeacoffee.com/abhaypratapsingh</a>
          </div>
          <div className="text-[10px]">No accounts • No names • The cloth is yours</div>
        </div>
      </footer>
    </BrowserRouter>
  )
}
